/**
 * In-memory API mock — used when `VITE_USE_MOCK === "true"`.
 *
 * Lets the SPA run without the Go backend, useful for design review and
 * Claude Preview demos. Mirrors the wire shapes from api-types.ts but
 * without the encryption / job queue / OAuth complexity.
 *
 * Behavior:
 *   - On import, seeds a fake authenticated user.
 *   - Every API path is matched against the routes table below.
 *   - Mutations persist to a module-level store (cleared on page reload).
 *   - PDF "generation" is faked: marks the job done after 3 seconds and
 *     returns a sample PDF URL.
 */

import { ApiError } from "./api";

// ---------------------------------------------------------------------------
// In-memory store
// ---------------------------------------------------------------------------

interface Store {
  user: {
    id: string;
    email: string;
    name: string;
  };
  cases: Map<string, CaseRow>;
  persons: Map<string, PersonRow>; // keyed by person id
  formResponses: Map<string, Map<string, unknown>>; // case_id → key → value
  jobs: Map<string, JobRow>;
  documents: Map<string, Map<string, Record<string, unknown>>>; // case_id → doc_id → doc
  // Sub-resource lists keyed by `${caseId}:${personId}:${resource}` → array of items
  nested: Map<string, Map<string, Record<string, unknown>>>; // bucket → itemId → item
}

interface CaseRow {
  id: string;
  case_type: string;
  status: string;
  applicant_person_id: string | null;
  beneficiary_person_id: string | null;
  eligibility_status: "eligible" | "out_of_scope" | "manual_review" | null;
  gating_answers?: Record<string, unknown>;
  paid_at?: string | null;
  fee_total_cents?: number | null;
  created_at: string;
  updated_at: string;
}

interface PersonRow {
  id: string;
  case_id: string;
  role: string;
  [k: string]: unknown;
}

interface JobRow {
  id: string;
  queue: string;
  case_id: string;
  state: "pending" | "running" | "done" | "failed";
  priority: number;
  attempts: number;
  max_attempts: number;
  scheduled_at: string;
  started_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  last_error: string | null;
  result: { status: string; output_key?: string; error_message?: string } | null;
  created_at: string;
  updated_at: string;
}

const store: Store = {
  user: {
    id: "user-mock-1",
    email: "you@example.com",
    name: "Mock User",
  },
  cases: new Map(),
  persons: new Map(),
  formResponses: new Map(),
  jobs: new Map(),
  documents: new Map(),
  nested: new Map(),
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const uuid = () =>
  "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
    ).toString(16),
  );

const now = () => new Date().toISOString();

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const ok = <T>(body: T) => Promise.resolve(body);

const fail = (status: number, message: string) =>
  Promise.reject(new ApiError(status, message, message));

function bucket(caseId: string, personId: string, resource: string): Map<string, Record<string, unknown>> {
  const k = `${caseId}:${personId}:${resource}`;
  let b = store.nested.get(k);
  if (!b) {
    b = new Map();
    store.nested.set(k, b);
  }
  return b;
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

interface MatchedRoute {
  handler: (params: Record<string, string>, body: unknown) => Promise<unknown>;
  params: Record<string, string>;
}

interface Route {
  method: string;
  pattern: RegExp;
  paramNames: string[];
  handler: (params: Record<string, string>, body: unknown) => Promise<unknown>;
}

const routes: Route[] = [];

function on(
  method: string,
  template: string,
  handler: (params: Record<string, string>, body: unknown) => Promise<unknown>,
) {
  // /v1/cases/:id/gate → regex with named groups.
  const paramNames: string[] = [];
  const pattern = new RegExp(
    "^" +
      template.replace(/:([a-zA-Z_]+)/g, (_, name) => {
        paramNames.push(name);
        return "([^/]+)";
      }) +
      "/?$",
  );
  routes.push({ method: method.toUpperCase(), pattern, paramNames, handler });
}

function match(method: string, path: string): MatchedRoute | null {
  // Strip query string.
  const cleanPath = path.split("?")[0];
  for (const r of routes) {
    if (r.method !== method.toUpperCase()) continue;
    const m = cleanPath.match(r.pattern);
    if (!m) continue;
    const params: Record<string, string> = {};
    r.paramNames.forEach((n, i) => (params[n] = m[i + 1]));
    return { handler: r.handler, params };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Route handlers
// ---------------------------------------------------------------------------

// auth
on("GET", "/v1/me", () => ok({ ...store.user }));
on("POST", "/v1/auth/logout", () => ok(undefined));

// cases
on("GET", "/v1/cases", () =>
  ok({ items: Array.from(store.cases.values()), total: store.cases.size }),
);
on("POST", "/v1/cases", (_p, body) => {
  const c: CaseRow = {
    id: uuid(),
    case_type: (body as { case_type: string }).case_type ?? "N400",
    status: "draft",
    applicant_person_id: null,
    beneficiary_person_id: null,
    eligibility_status: null,
    created_at: now(),
    updated_at: now(),
  };
  store.cases.set(c.id, c);
  return ok(c);
});
on("GET", "/v1/cases/:id", ({ id }) => {
  const c = store.cases.get(id);
  return c ? ok(c) : fail(404, "not found");
});
on("POST", "/v1/cases/:id/gate", ({ id }, body) => {
  const c = store.cases.get(id);
  if (!c) return fail(404, "not found");
  const a = body as Record<string, boolean>;

  // Mirror N400GatingAnswers.evaluate() logic.
  const checks: Array<[string, boolean]> = [
    ["has_criminal_history", !!a.has_criminal_history],
    ["has_prior_uscis_denial", !!a.has_prior_uscis_denial],
    ["has_removal_proceedings", !!a.has_removal_proceedings],
    ["continuous_us_residence", !a.continuous_us_residence],
    ["meets_lpr_duration", !a.meets_lpr_duration],
    ["is_self_filing", !a.is_self_filing],
    ["currently_in_us", !a.currently_in_us],
  ];
  let status: CaseRow["eligibility_status"] = "eligible";
  let dq: string | undefined;
  for (const [key, fail] of checks) {
    if (fail) {
      status = "out_of_scope";
      dq = key;
      break;
    }
  }
  c.eligibility_status = status;
  c.gating_answers = a;
  c.updated_at = now();
  return ok({ eligibility_status: status, disqualifying_question: dq, case: c });
});
on("GET", "/v1/cases/:id/evaluation", ({ id }) => {
  const c = store.cases.get(id);
  if (!c) return fail(404, "not found");
  // Simple heuristic: ready if applicant has name + DOB and there's a current address.
  const applicant = c.applicant_person_id
    ? (store.persons.get(c.applicant_person_id) as PersonRow & {
        family_name?: string;
        given_name?: string;
        date_of_birth?: string;
      })
    : null;
  const missing: { key: string; message: string }[] = [];
  if (!applicant?.given_name) missing.push({ key: "given_name", message: "Given name required" });
  if (!applicant?.family_name) missing.push({ key: "family_name", message: "Family name required" });
  if (!applicant?.date_of_birth)
    missing.push({ key: "date_of_birth", message: "Date of birth required" });
  if (applicant) {
    const addrs = bucket(id, applicant.id, "addresses");
    const hasCurrent = Array.from(addrs.values()).some(
      (a) => (a as { is_physical?: boolean; end_date?: string }).is_physical && !(a as { end_date?: string }).end_date,
    );
    if (!hasCurrent) missing.push({ key: "current_address", message: "Current physical address required" });
  } else {
    missing.push({ key: "applicant", message: "Applicant info required" });
  }

  return ok({
    ready: missing.length === 0,
    eligibility_status: c.eligibility_status,
    required_forms: ["N-400"],
    missing_data: missing,
    format_errors: [],
    warnings: [],
  });
});

// checkout / payment
on("POST", "/v1/cases/:id/checkout", ({ id }) => {
  const c = store.cases.get(id);
  if (!c) return fail(404, "not found");
  if (c.eligibility_status !== "eligible") return fail(422, "not eligible");
  if (c.paid_at) return fail(409, "case is already paid");

  const sessionID = "cs_mock_" + Math.random().toString(36).slice(2);
  // Store the fee so it shows up in the UI.
  c.fee_total_cents = 71000;
  c.updated_at = now();
  store.cases.set(id, c);

  // In mock mode the "checkout URL" is just a sentinel — StepCheckout
  // detects isMockEnabled and never actually redirects there.
  return ok({ checkout_url: "#mock-stripe-redirect", session_id: sessionID });
});

on("GET", "/v1/cases/:id/dev-mark-paid", ({ id }) => {
  const c = store.cases.get(id);
  if (!c) return fail(404, "not found");
  c.paid_at = now();
  c.fee_total_cents = c.fee_total_cents ?? 71000;
  c.updated_at = now();
  store.cases.set(id, c);
  return ok(c);
});

// generate / jobs
on("POST", "/v1/cases/:id/generate", ({ id }) => {
  const c = store.cases.get(id);
  if (!c) return fail(404, "not found");
  if (c.eligibility_status !== "eligible") return fail(422, "not eligible");
  if (!c.paid_at) return fail(402, "filing fee not paid — complete checkout before generating");

  const job: JobRow = {
    id: uuid(),
    queue: "pdf.render",
    case_id: id,
    state: "pending",
    priority: 0,
    attempts: 0,
    max_attempts: 3,
    scheduled_at: now(),
    started_at: null,
    completed_at: null,
    failed_at: null,
    last_error: null,
    result: null,
    created_at: now(),
    updated_at: now(),
  };
  store.jobs.set(job.id, job);

  // Simulate the worker: pending → running → done.
  void (async () => {
    await wait(1000);
    job.state = "running";
    job.started_at = now();
    job.attempts = 1;
    job.updated_at = now();
    await wait(2500);
    job.state = "done";
    job.completed_at = now();
    job.result = {
      status: "success",
      output_key: `cases/${id}/packets/v1/packet.pdf`,
    };
    job.updated_at = now();
  })();

  return ok({
    job_id: job.id,
    queue: job.queue,
    form_code: "N-400",
    edition: "2025-01-20",
    message: "PDF render job queued",
  });
});
on("GET", "/v1/cases/:id/jobs", ({ id }) => {
  const items = Array.from(store.jobs.values()).filter((j) => j.case_id === id);
  return ok({ items, total: items.length });
});
on("GET", "/v1/cases/:id/jobs/:jid", ({ jid }) => {
  const j = store.jobs.get(jid);
  return j ? ok(j) : fail(404, "not found");
});
on("GET", "/v1/cases/:id/jobs/:jid/download", ({ jid }) => {
  const j = store.jobs.get(jid);
  if (!j || j.state !== "done") return fail(409, "job not complete yet");
  return ok({
    // Sample USCIS form PDF — public domain.
    download_url: "https://www.uscis.gov/sites/default/files/document/forms/n-400.pdf",
    file_key: j.result?.output_key ?? "cases/mock/packet.pdf",
    expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
  });
});

// persons
on("GET", "/v1/cases/:cid/persons", ({ cid }) => {
  const items = Array.from(store.persons.values()).filter((p) => p.case_id === cid);
  return ok({ items, total: items.length });
});
on("POST", "/v1/cases/:cid/persons", ({ cid }, body) => {
  const c = store.cases.get(cid);
  if (!c) return fail(404, "not found");
  const p: PersonRow = {
    id: uuid(),
    case_id: cid,
    role: (body as { role?: string }).role ?? "applicant",
    ...(body as Record<string, unknown>),
    created_at: now(),
    updated_at: now(),
  };
  store.persons.set(p.id, p);
  if (p.role === "applicant" && !c.applicant_person_id) {
    c.applicant_person_id = p.id;
    c.updated_at = now();
  }
  return ok(p);
});
on("PATCH", "/v1/cases/:cid/persons/:pid", ({ pid }, body) => {
  const p = store.persons.get(pid);
  if (!p) return fail(404, "not found");
  Object.assign(p, body as Record<string, unknown>, { updated_at: now() });
  return ok(p);
});
on("GET", "/v1/cases/:cid/persons/:pid", ({ pid }) => {
  const p = store.persons.get(pid);
  return p ? ok(p) : fail(404, "not found");
});

// generic nested CRUD: addresses, employments, travels, marriages, children, criminal-events
const NESTED = ["addresses", "employments", "travels", "marriages", "children", "criminal-events"];
for (const r of NESTED) {
  on("GET", `/v1/cases/:cid/persons/:pid/${r}`, ({ cid, pid }) => {
    const items = Array.from(bucket(cid, pid, r).values());
    return ok({ items, total: items.length });
  });
  on("POST", `/v1/cases/:cid/persons/:pid/${r}`, ({ cid, pid }, body) => {
    const item = {
      id: uuid(),
      person_id: pid,
      ...(body as Record<string, unknown>),
      created_at: now(),
      updated_at: now(),
    };
    if (r === "children") (item as unknown as { parent_person_id: string }).parent_person_id = pid;
    bucket(cid, pid, r).set(item.id, item);
    return ok(item);
  });
  on("PUT", `/v1/cases/:cid/persons/:pid/${r}/:itemId`, ({ cid, pid, itemId }, body) => {
    const b = bucket(cid, pid, r);
    if (!b.has(itemId)) return fail(404, "not found");
    const next = {
      ...b.get(itemId),
      ...(body as Record<string, unknown>),
      id: itemId,
      person_id: pid,
      updated_at: now(),
    };
    b.set(itemId, next);
    return ok(next);
  });
  on("DELETE", `/v1/cases/:cid/persons/:pid/${r}/:itemId`, ({ cid, pid, itemId }) => {
    bucket(cid, pid, r).delete(itemId);
    return ok(undefined);
  });
}

// form-responses
on("GET", "/v1/cases/:id/form-responses", ({ id }) => {
  const m = store.formResponses.get(id) ?? new Map();
  const items = Array.from(m.entries()).map(([key, value]) => ({
    id: `fr-${key}`,
    case_id: id,
    form_type: "N-400",
    form_edition: "2025-01-20",
    field_key: key,
    value,
    created_at: now(),
    updated_at: now(),
  }));
  return ok({ items, total: items.length });
});
on(
  "PUT",
  "/v1/cases/:id/form-responses/:formType/:formEdition/:fieldKey",
  ({ id, fieldKey }, body) => {
    // Frontend sends `{value: <any>}` per the backend
    // FormResponseRequest shape — unwrap before storing.
    const value = (body as { value: unknown })?.value;
    let m = store.formResponses.get(id);
    if (!m) {
      m = new Map();
      store.formResponses.set(id, m);
    }
    m.set(fieldKey, value);
    return ok({
      id: `fr-${fieldKey}`,
      case_id: id,
      form_type: "N-400",
      form_edition: "2025-01-20",
      field_key: fieldKey,
      value,
      created_at: now(),
      updated_at: now(),
    });
  },
);
on(
  "DELETE",
  "/v1/cases/:id/form-responses/:formType/:formEdition/:fieldKey",
  ({ id, fieldKey }) => {
    store.formResponses.get(id)?.delete(fieldKey);
    return ok(undefined);
  },
);

// documents
on("GET", "/v1/cases/:id/documents", ({ id }) => {
  const docs = store.documents.get(id) ?? new Map();
  const items = Array.from(docs.values());
  return ok({ items, total: items.length });
});

on("POST", "/v1/cases/:id/documents", ({ id }, body) => {
  let docs = store.documents.get(id);
  if (!docs) { docs = new Map(); store.documents.set(id, docs); }
  const docId = uuid();
  const doc = {
    id: docId,
    case_id: id,
    file_name_original: (body as Record<string, unknown>)?.file_name ?? "document.pdf",
    mime_type: (body as Record<string, unknown>)?.mime_type ?? "application/pdf",
    size_bytes: (body as Record<string, unknown>)?.size_bytes ?? 102400,
    document_type: (body as Record<string, unknown>)?.document_type ?? null,
    processing_state: "pending",
    created_at: now(),
    updated_at: now(),
  };
  docs.set(docId, doc);
  return ok(doc);
});

on("DELETE", "/v1/cases/:id/documents/:did", ({ id, did }) => {
  store.documents.get(id)?.delete(did);
  return ok(undefined);
});

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * mockApiFetch is called from lib/api.ts when VITE_USE_MOCK === "true".
 * Returns a JSON-serialized response shape compatible with the real API.
 */
export async function mockApiFetch<T>(
  path: string,
  options: { method?: string; json?: unknown } = {},
): Promise<T> {
  const m = match(options.method ?? "GET", path);
  if (!m) {
    console.warn(`[mock] no route for ${options.method ?? "GET"} ${path}`);
    throw new ApiError(404, `mock: no route for ${path}`, null);
  }
  // 50ms latency to feel realistic.
  await wait(50);
  const result = await m.handler(m.params, options.json);
  return result as T;
}

export const isMockEnabled = import.meta.env.VITE_USE_MOCK === "true";
